/**
 * Clear Graduated Students ClassIds (Simple Version)
 * Removes classId and className from graduated/inactive students
 * This version gets schoolId from any available source
 *
 * USAGE: await window.clearGraduatedSimple()
 */

import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase.js';

async function clearGraduatedSimple() {
  console.log('🎓 Clearing classId from graduated students...\n');

  try {
    // Try to get schoolId from multiple sources
    let schoolId = null;

    // Method 1: Look for any swan_school_context key in localStorage
    const keys = Object.keys(localStorage);
    const contextKey = keys.find(k => k.startsWith('swan_school_context_'));

    if (contextKey) {
      const contextData = localStorage.getItem(contextKey);
      if (contextData) {
        const parsed = JSON.parse(contextData);
        schoolId = parsed.schoolId;
        console.log(`📚 Found school ID from ${contextKey}: ${schoolId}`);
      }
    }

    // Method 2: Try to get from URL if on a school page
    if (!schoolId && window.location.pathname.includes('/schools/')) {
      const match = window.location.pathname.match(/schools\/([^/]+)/);
      if (match) {
        schoolId = match[1];
        console.log(`📚 Found school ID from URL: ${schoolId}`);
      }
    }

    if (!schoolId) {
      console.error('❌ Could not find school ID. Please make sure you are logged in and on a school page.');
      console.log('Available localStorage keys:', keys.filter(k => k.includes('school')));
      return { success: false, error: 'No schoolId found' };
    }

    // Query for all students in the school
    const studentsRef = collection(db, `schools/${schoolId}/students`);
    const snapshot = await getDocs(studentsRef);

    console.log(`Found ${snapshot.size} total students`);

    // Find graduated/inactive students with classId still set
    const toFix = [];
    snapshot.forEach(doc => {
      const student = { id: doc.id, ...doc.data() };
      if ((student.status === 'graduated' || student.status === 'inactive') && student.classId) {
        toFix.push(student);
      }
    });

    console.log(`Found ${toFix.length} graduated/inactive students with classId set`);

    if (toFix.length === 0) {
      console.log('✅ No students need fixing!');
      return { success: true, fixed: 0 };
    }

    // Show what we'll fix
    console.log('\nStudents to clear:');
    toFix.forEach(s => {
      console.log(`  - ${s.firstName} ${s.surname} (${s.status}, was in ${s.className || 'unknown class'})`);
    });

    // Ask for confirmation
    console.log('\n⚠️  This will clear classId from these students.');
    console.log('To proceed, run: await window.clearGraduatedConfirm()');

    // Store the list for confirmation function
    window._toFixStudents = { schoolId, students: toFix };

    return { success: true, found: toFix.length, pending: true };

  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
}

// Confirmation function
async function clearGraduatedConfirm() {
  if (!window._toFixStudents) {
    console.error('❌ No pending students to fix. Run clearGraduatedSimple() first.');
    return;
  }

  const { schoolId, students } = window._toFixStudents;

  console.log(`\n🔄 Clearing classId from ${students.length} students...`);

  for (const student of students) {
    console.log(`  Clearing: ${student.firstName} ${student.surname}`);
    const studentRef = doc(db, `schools/${schoolId}/students`, student.id);
    await updateDoc(studentRef, {
      classId: null,
      className: null
    });
  }

  console.log('\n✅ Complete! Graduated/inactive students no longer have classIds.');
  delete window._toFixStudents;

  return { success: true, fixed: students.length };
}

export { clearGraduatedSimple, clearGraduatedConfirm };